import { Button, Stack } from '@mui/material';
import { TixSelector } from '@tix-ticketing/ui-components';
import { CheckIcon, CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { bindToggle, usePopupState } from 'material-ui-popup-state/hooks';
import { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import type { CalendarRange } from 'components/events-list/calendar-multi';
import { CalendarMulti } from 'components/events-list/calendar-multi';
import { EventFilterButton } from 'components/events-list/event-filter-button';
import { clientTimezone } from 'lib/helpers/date-helpers';
export const EventsPaginatedDateRangeFilterSelector = ({
  dateRange,
  setDateRange,
  earliestEventDate
}: {
  dateRange: CalendarRange;
  setDateRange: (range: CalendarRange) => void;
  earliestEventDate?: Date | null;
}) => {
  const dateRangeState = usePopupState({
    variant: 'popper',
    popupId: 'dateRangeFilterSelector'
  });

  // The picker edits a draft; nothing is applied to the list until "Apply".
  const [draft, setDraft] = useState<CalendarRange>(dateRange);

  // Re-sync the draft with the applied range whenever the popover opens, so a
  // dismissed (un-applied) selection doesn't linger on the next open.
  useEffect(() => {
    if (dateRangeState.isOpen) {
      setDraft(dateRange);
    }
  }, [dateRangeState.isOpen, dateRange]);
  const [start, end] = dateRange;
  const isActive = start !== null;
  const label = isActive ? <span>
      <FormattedDate value={start} day="numeric" month="short" timeZone={clientTimezone} />
      {end && <>
          {' – '}
          <FormattedDate value={end} day="numeric" month="short" timeZone={clientTimezone} />
        </>}
    </span> : <FormattedMessage id="event.filter.dateRange" defaultMessage={[{
    "type": 0,
    "value": "Date range"
  }]} />;
  return <TixSelector triggerElement={<EventFilterButton active={dateRangeState.isOpen} clearable={isActive} onClear={() => {
    dateRangeState.close();
    setDateRange([null, null]);
  }} label={label} {...bindToggle(dateRangeState)} />} open={dateRangeState.isOpen} onClose={() => dateRangeState.close()} localeState={dateRangeState} hideCloseButton popperPlacement="bottom-start" popperMaxWidth={720}>
      <Stack sx={{
      gap: 2
    }}>
        <CalendarMulti startDate={draft[0]} endDate={draft[1]} onChange={setDraft} initialViewMonth={earliestEventDate} />
        <Stack direction="row" sx={{
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1
      }}>
          <Button variant="text" color="monochrome" startIcon={<CrossIcon />} onClick={() => setDraft([null, null])} disabled={draft[0] === null}>
            <FormattedMessage id="common.clear" defaultMessage={[{
            "type": 0,
            "value": "Clear"
          }]} />
          </Button>
          <Button variant="contained" color="primary" startIcon={<CheckIcon />} onClick={() => {
          setDateRange(draft);
          dateRangeState.close();
        }} disabled={draft[0] === null}>
            <FormattedMessage id="common.apply" defaultMessage={[{
            "type": 0,
            "value": "Apply"
          }]} />
          </Button>
        </Stack>
      </Stack>
    </TixSelector>;
};